/**
 * ReaderSidebarShell — Shared left sidebar wrapper for reader pages
 *
 * Guarantees visual consistency between Content (S&M) and Documentation (Product)
 * sidebars: w-64, warm gradient bg, h-12 header, scrollable body, footer count.
 */

import type { ReactNode } from 'react';

interface ReaderSidebarShellProps {
  title: string;
  headerActions: ReactNode;
  children: ReactNode;
  /** String = auto-styled count text; ReactNode = custom footer */
  footer?: ReactNode | string;
  isLoading?: boolean;
  loadingSkeleton?: ReactNode;
  className?: string;
}

const DefaultLoadingSkeleton = () => (
  <div className="space-y-4 px-2 py-2 animate-pulse">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="space-y-1.5">
        <div className="h-7 bg-surface-subtle rounded-lg" />
        <div className="flex gap-2 ml-6">
          <div className="h-3 w-12 bg-surface-subtle rounded-full" />
          <div className="h-3 w-16 bg-surface-muted rounded" />
        </div>
      </div>
    ))}
  </div>
);

export function ReaderSidebarShell({
  title,
  headerActions,
  children,
  footer,
  isLoading = false,
  loadingSkeleton,
  className = '',
}: ReaderSidebarShellProps) {
  return (
    <aside
      className={`w-64 flex-shrink-0 border-r border-line/60 bg-gradient-to-b from-white via-white to-stone-50/50 flex flex-col h-full overflow-hidden ${className}`}
    >
      {/* Header bar — h-12 aligns with center + right panels */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-line/60 bg-white/80 shrink-0">
        <h2 className="text-[13px] font-semibold text-text">{title}</h2>
        <div className="flex items-center gap-1">
          {headerActions}
        </div>
      </div>

      {/* Scrollable body */}
      {isLoading ? (
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {loadingSkeleton || <DefaultLoadingSkeleton />}
        </div>
      ) : (
        <>{children}</>
      )}

      {/* Footer */}
      {footer !== undefined && (
        <div className="p-3 border-t border-line/40 shrink-0">
          {typeof footer === 'string' ? (
            <p className="text-[10px] text-subtle text-center">{footer}</p>
          ) : (
            footer
          )}
        </div>
      )}
    </aside>
  );
}

export default ReaderSidebarShell;
